import BigNumber from "bignumber.js";
import moment, { Moment } from "moment";
import { FrontDate } from "../domain/model/frontDate";
import { TFileObject } from "../presentation/type/common";

// 年月日の比較
// ２つの年月日の比較を行い、startedAt　＜ endedAt の場合trueを返信する
// 前提：
// 　startedAtは最低限年の入力がある事
//   endedAtは値を持たない場合、絶対的な未来を指す事とする
// 前提を満たさない場合は　false を返す
export const isDateStartBeforeEnd = (
  startedAt: FrontDate,
  endedAt: FrontDate
): boolean => {
  // 前提確認
  if (startedAt.year === "") {
    return false;
  }
  if (endedAt.year === "") {
    return true;
  }

  // 年比較
  if (Number(startedAt.year) < Number(endedAt.year)) {
    return true;
  }
  if (Number(startedAt.year) > Number(endedAt.year)) {
    return false;
  }

  // 月比較
  if (Number(startedAt.month) < Number(endedAt.month)) {
    return true;
  }
  if (Number(startedAt.month) > Number(endedAt.month)) {
    return false;
  }

  // 日比較
  if (Number(startedAt.day) < Number(endedAt.day)) {
    return true;
  }
  if (Number(startedAt.day) > Number(endedAt.day)) {
    return false;
  }

  return true;
};

// 年月から歳を算出する
export const calcAgeByBirthDay = (birthdate: FrontDate): number => {
  const birthdate_moment: Moment = moment()
    .year(Number(birthdate.year))
    .month(Number(birthdate.month) - 1)
    .date(Number(birthdate.day));

  return moment().diff(birthdate_moment, "years");
};

// 現在の年を基準に指定年前迄の年配列を作成する
export const createYearsArray = (
  count: number,
  startYear?: number
): string[] => {
  const yearStart: number = startYear
    ? startYear
    : Number(moment().year().toString());
  const resArray: string[] = [];
  resArray.push("");

  for (let i = 0; i < count; i++) {
    resArray.push((yearStart - i).toString());
  }
  return resArray;
};

export const createMonthArray = (): string[] => {
  const monthStart: number = 1;
  const resArray: string[] = [];
  resArray.push("");

  for (let i = 0; i < 12; i++) {
    resArray.push(("00" + (monthStart + i).toString()).slice(-2));
  }
  return resArray;
};

export const createDayArray = (
  endDay?: number,
  isNumber?: boolean
): string[] => {
  const dayStart: number = 1;
  const dayEnd: number = endDay ? endDay : 31;
  const resArray: string[] = [];
  resArray.push("");

  if (!isNumber) {
    for (let i = 0; i < dayEnd; i++) {
      resArray.push(("00" + (dayStart + i).toString()).slice(-2));
    }
  }
  for (let i = 0; i < dayEnd; i++) {
    resArray.push((dayStart + i).toString());
  }
  return resArray;
};

export const createHourArray = (endHour?: number): string[] => {
  const hourStart: number = 1;
  const hourEnd: number = endHour ? endHour : 24;
  const resArray: string[] = [];
  resArray.push("");

  for (let i = 0; i < hourEnd; i++) {
    resArray.push((hourStart + i).toString().slice(-2));
  }
  return resArray;
};

// 2つの配列オブジェクトをもらって中身の差分（配列オブジェクト）を返すarray1 にあって array2にない要素を返す
export const diffFileObject = (
  array1: TFileObject[],
  array2: TFileObject[]
) => {
  const concatArray = array1.concat(array2);

  const res: TFileObject[] = [];
  concatArray.forEach((item) => {
    if (array1.includes(item) && !array2.includes(item)) {
      res.push(item);
    }
  });
  return res;
};

export const prettyNumber = (pBytes: number, pUnits?: string) => {
  // Handle some special cases
  if (pBytes === 0) {
    return "0 Bytes";
  }
  if (pBytes === 1) {
    return "1 Byte";
  }
  if (pBytes === -1) {
    return "-1 Byte";
  }

  const bytes = Math.abs(pBytes);
  let orderOfMagnitude;
  let abbreviations;
  if (pUnits && pUnits.toLowerCase() && pUnits.toLowerCase() === "si") {
    // SI units use the Metric representation based on 10^3 as a order of magnitude
    orderOfMagnitude = Math.pow(10, 3);
    abbreviations = ["Bytes", "kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  } else {
    // IEC units use 2^10 as an order of magnitude
    orderOfMagnitude = Math.pow(2, 10);
    abbreviations = [
      "Bytes",
      "KiB",
      "MiB",
      "GiB",
      "TiB",
      "PiB",
      "EiB",
      "ZiB",
      "YiB",
    ];
  }
  const i = Math.floor(Math.log(bytes) / Math.log(orderOfMagnitude));
  let result = bytes / Math.pow(orderOfMagnitude, i);

  // This will get the sign right
  if (pBytes < 0) {
    result *= -1;
  }

  // This bit here is purely for show. it drops the percision on numbers greater than 100 before the units.
  // it also always shows the full number of bytes if bytes is the unit.
  if (result >= 99.995 || i === 0) {
    return result.toFixed(0) + " " + abbreviations[i];
  } else {
    return result.toFixed(2) + " " + abbreviations[i];
  }
};

// 消費税
export const sumSalesTaxNumber = ({
  salesTaxSubTotalNumber,
  salesTaxRateNumber,
}: {
  salesTaxSubTotalNumber: number;
  salesTaxRateNumber: number;
}) => {
  const sum = new BigNumber(salesTaxSubTotalNumber)
    .times(salesTaxRateNumber)
    .dividedBy(100)
    .toNumber();
  return Math.floor(sum);
};

// 源泉徴収
export const sumWithholdingTaxNumber = ({
  salesTaxSubTotalNumber,
  withholdingTaxRateNumber,
}: {
  salesTaxSubTotalNumber: number;
  withholdingTaxRateNumber: number;
}) => {
  const sum = new BigNumber(salesTaxSubTotalNumber)
    .times(withholdingTaxRateNumber)
    .dividedBy(100)
    .toNumber();
  return Math.floor(sum);
};

// 合計 小計＋消費税-源泉徴収
export const sumTotalAmountValueNumber = ({
  subTotalNumber,
  salesTaxNumber,
  withholdingTaxNumber,
}: {
  subTotalNumber: number;
  salesTaxNumber: number;
  withholdingTaxNumber: number;
}) => {
  const sum = new BigNumber(subTotalNumber)
    .plus(salesTaxNumber)
    .minus(withholdingTaxNumber)
    .toNumber();
  return Math.floor(sum);
};

export const toDateString = (from: string): string => {
  if (from === "") {
    return "";
  }

  const date: FrontDate = FrontDate.from(from);
  return `${date.year}年${date.month}月${date.day}日`;
};

export const toYMString = (input: string): string => {
  if (input === "") {
    return "";
  }

  const parts = input.split("-");
  const year = parts[0];
  let month = parts[1];

  // 月が一桁の場合、先頭に0を追加
  if (month.length === 1) {
    month = "0" + month;
  }

  return `${year}年${month}月`;
};

export const ymToDate = (from: string): Date => {
  if (from === "") {
    return new Date();
  }

  const parts = from.split("-");
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);

  return new Date(year, month - 1);
};

export const ymToYear = (from: string): number | undefined => {
  if (from === "") {
    return;
  }

  const parts = from.split("-");
  const year = parts[0];

  return parseInt(year, 10);
};

export const ymToMonth = (from: string): number | undefined => {
  if (from === "") {
    return;
  }

  const parts = from.split("-");
  const month = parts[1];

  return parseInt(month, 10);
};



// WEBPACK FOOTER //
// ./src/util/commonFuntion.ts